"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { getCookie } from "../util/getCookie";
import { isProduction } from "../util/EnvUtils";
type CookieListener = (value: string | undefined) => void;
type CookieListenerData = {
  value: string | undefined;
  listeners: CookieListener[];
};
const POLL_INTERVAL_MS = 1000;
const listenerTable: Record<string, CookieListenerData> = {};
let pollIntervalHandle: ReturnType<typeof setInterval> | undefined = undefined;

// Internal helper to start polling for cookie changes.
function startPolling() {
  if (pollIntervalHandle == null) {
    pollIntervalHandle = setInterval(() => {
      for (const k of Object.keys(listenerTable)) {
        const currValue = getCookie(k);
        const listenerData = listenerTable[k]!;
        if (currValue !== listenerData.value) {
          listenerData.value = currValue;
          listenerData.listeners.forEach(f => f(currValue));
        }
      }
    }, POLL_INTERVAL_MS);
  }
}

// Internal helper to add a listener to the cookie change polling. Also starts
// the polling if not already.
function addListener(name: string, newListener: CookieListener) {
  if (listenerTable[name]) {
    listenerTable[name].listeners.push(newListener);
  } else {
    listenerTable[name] = {
      value: getCookie(name),
      listeners: [newListener]
    };
  }
  startPolling();
}

// Internal helper to unsubscribe from polling.
function removeListener(name: string, toRemove: CookieListener) {
  if (listenerTable[name]) {
    listenerTable[name].listeners = listenerTable[name].listeners.filter(f => f !== toRemove);
    if (!listenerTable[name].listeners.length) {
      delete listenerTable[name];
    }
    if (!Object.keys(listenerTable).length) {
      clearInterval(pollIntervalHandle);
      pollIntervalHandle = undefined;
    }
  }
}

/**
 * A hook to get the value of a cookie, and trigger rerenders if the value of
 * that cookie changes. Wraps the `getCookie` utility.
 *
 * It uses a once-per-second polling approach to detect changes.
 *
 * In production, returns undefined immediately without any polling or cookie
 * reading.
 */
export function useWatchedCookie(name) {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = isProduction();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isProd = t0;
  let t1;
  if ($[1] !== name) {
    t1 = () => {
      if (isProd) {
        return;
      }
      return getCookie(name);
    };
    $[1] = name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [returnedValue, setReturnedValue] = useState(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = value => {
      if (!isProd) {
        setReturnedValue(value);
      }
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onChange = t2;
  let t3;
  let t4;
  if ($[4] !== name) {
    t3 = () => {
      if (isProd) {
        return;
      }
      const value_0 = getCookie(name);
      onChange(value_0);
      addListener(name, onChange);
      return () => removeListener(name, onChange);
    };
    t4 = [name, onChange, isProd];
    $[4] = name;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  return returnedValue;
}